import {
  Avatar,
  Box,
  Button,
  Divider,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';
import { IDocument, getFile } from 'API';
import { DICTIONARY } from 'Enum';
import { Dictionary } from 'Dictionary';
import { Download2, PurplePdfDocument } from 'Images';
import { NewItemBadge } from 'Components';
import { format, isAfter } from 'date-fns';
import { shortDateFormat } from 'Config';
import { downloadFile, getFileMimeCode } from 'Utils';
import { useDictionary, useIsDesktop } from 'Hooks';
import { useMemo } from 'react';
import { MIN_NEW_DATE } from '../DocumentsMain';
import { useLoader } from 'Hooks/useLoader';
import { useEffect } from 'react';

interface IDocumentItem {
  document: IDocument;
}

export const DocumentItem = ({ document }: IDocumentItem) => {
  const { isLoading, loader, setIsLoading } = useLoader('scale(0.5)');

  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.Documents);
  const generalDictionary = useDictionary(Dictionary, DICTIONARY.General);

  useEffect(() => {
    setIsLoading(false);
  }, [setIsLoading]);

  const isDocumentNew: boolean = useMemo(() => {
    const today = new Date();
    const documentDate = new Date(document.createDate);
    const minNew = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - MIN_NEW_DATE
    );

    return isAfter(documentDate, minNew);
  }, [document.createDate]);

  const watchFile = async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    setIsLoading(true);
    e.stopPropagation();

    const file = await getFile(document.id);
    if (file) {
      if (file.fileContentBase64.indexOf(getFileMimeCode('pdf')) === 0)
        downloadFile(file.fileContentBase64, 'pdf', document.documentName);
      else
        downloadFile(
          file?.fileContentBase64,
          '',
          file.documentName || 'New Document'
        );
    }
    setIsLoading(false);
  };

  const layoutStyles: SxProps = isDesktop
    ? {
        gap: '48px',
        maxWidth: '833px',
        display: 'flex',
        flexWrap: 'wrap',
      }
    : {
        padding: '16px',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gridTemplateRows: 'auto',
        gridTemplateAreas: `"documentName documentName"
        "divider divider"
        "documentTitle documentDate"
        "downloadButton downloadButton"`,
      };

  return (
    <Stack>
      <Box>
        <Box
          sx={{
            width: '100%',
            background: '#FFFFFF',
            cursor: 'pointer',
            padding: isDesktop ? '32px 24px' : '0',
            position: 'relative',
            borderRadius: '16px',
            border: (theme) => `1px solid ${theme.palette.primary.shade_100}`,
            boxShadow: '-2px -2px 12px rgba(8, 30, 84, 0.04)',
            display: isDesktop ? 'flex' : 'block',
          }}
        >
          <Box onClick={watchFile} sx={layoutStyles}>
            <Box
              sx={{
                gridArea: 'documentName',
                width: isDesktop ? '275px' : 'auto',
              }}
            >
              <Stack gap='4px'>
                <Typography variant='body7' component='h4'>
                  {dictionary.document.name}
                </Typography>
                <Stack direction='row' alignItems='center' gap='6px'>
                  <Avatar
                    src={PurplePdfDocument}
                    alt=''
                    sx={{ height: '20px', width: '20px' }}
                  />
                  <Typography variant='body1'>
                    {document.documentName}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
            {!isDesktop && (
              <Divider
                orientation='horizontal'
                aria-hidden={'true'}
                sx={{
                  borderColor: 'primary.shade_100',
                  gridArea: 'divider',
                  margin: '16px 0',
                }}
              />
            )}

            <Box
              sx={{
                gridArea: 'documentTitle',
                marginBottom: isDesktop ? '0px' : '10px',
                width: isDesktop ? '210px' : 'auto',
              }}
            >
              <Stack gap='4px'>
                <Typography variant='body7'>
                  {dictionary.document.title}
                </Typography>
                <Typography variant='body5'>
                  {Dictionary[
                    DICTIONARY.Documents
                  ].document.documentTitleNameTransformation(
                    document.subjectName
                  )}
                </Typography>
              </Stack>
            </Box>
            <Box
              gridArea={'documentDate'}
              width={'90px'}
              justifySelf={isDesktop ? undefined : 'center'}
            >
              <Stack gap='4px'>
                <Typography variant='body7'>
                  {dictionary.document.uploadDate}
                </Typography>
                <Typography variant='body5'>
                  {format(new Date(document.createDate), shortDateFormat)}
                </Typography>
              </Stack>
            </Box>
            <Box
              sx={{
                gridArea: 'downloadButton',
                width: isDesktop ? '110px' : '100%',
                height: isDesktop ? undefined : '44px',
                display: 'grid',
                placeContent: 'center',
              }}
            >
              {isLoading ? (
                <Stack height={'20px'} justifyContent={'center'}>
                  {loader}
                </Stack>
              ) : (
                <Button
                  onClick={watchFile}
                  sx={{
                    border: (theme) =>
                      isDesktop
                        ? `1px solid ${theme.palette.primary.shade_200}`
                        : 'none',
                    position: isDesktop ? 'static' : 'absolute',
                    bottom: isDesktop ? 'unset' : '0',
                    left: isDesktop ? 'unset' : '0',
                    width: isDesktop ? '112px' : '100%',
                    height: isDesktop ? '46px' : '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isDesktop ? 'center' : 'flex-end',
                    gap: '6px',
                    padding: isDesktop ? '12px 24px' : '0 24px 0 0',
                    borderRadius: isDesktop ? '100px' : '0',
                    borderBottomLeftRadius: isDesktop ? '100px' : '16px',
                    borderBottomRightRadius: isDesktop ? '100px' : '16px',
                    boxShadow: '0px 2px 16px rgba(4, 16, 46, 0.08)',
                    background: (theme) => `${theme.palette.primary.shade_100}`,
                    '&:hover': {
                      background: (theme) =>
                        `${theme.palette.primary.shade_100}`,
                    },
                  }}
                  aria-label={`${generalDictionary.watchShort} ${document.documentName}`}
                >
                  <Avatar
                    src={Download2}
                    alt={''}
                    sx={{ width: '20px', height: '20px' }}
                  />
                  <Typography variant={isDesktop ? 'body4' : 'body5'}>
                    {generalDictionary.watchShort}
                  </Typography>
                </Button>
              )}
            </Box>
          </Box>
        </Box>
        {isDocumentNew && (
          <NewItemBadge
            boxStyles={{ top: isDesktop ? '0px' : '8px', right: '-16px' }}
          />
        )}
      </Box>
    </Stack>
  );
};
